import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type {
  CatalogueFeature,
  FeatureCopyPayload,
  FeatureInstitute,
  FeatureCopyResult,
  FeatureSwitchPayload,
  InstituteFeatureBoard,
  PlatformFeature,
} from '@/types/platformFeature';
import type { CatalogueEditPayload, CatalogueEntryPayload } from '../schemas/feature.schema';

/**
 * Every network call the feature tracker makes. Components never touch
 * `apiClient` directly (CLAUDE.md §6).
 *
 * Each write answers with the institute's whole board rather than the row it
 * touched, so the screen replaces its state instead of patching it — a total
 * can never end up disagreeing with the rows under it.
 */
export const featuresApi = {
  /** The catalogue: everything the product does, grouped and in order. */
  catalogue: () => unwrap(apiClient.get<ApiSuccess<CatalogueFeature[]>>('/platform/features')),

  /** The institutes that have one feature, for the catalogue's "requested by" panel. */
  institutesWith: (uuid: string) =>
    unwrap(apiClient.get<ApiSuccess<FeatureInstitute[]>>(`/platform/features/${uuid}/institutes`)),

  /** Soft-deletes a feature. Refused while any institute still has it. */
  removeFromCatalogue: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/platform/features/${uuid}`)),

  addToCatalogue: (payload: CatalogueEntryPayload) =>
    unwrap(
      apiClient.post<ApiSuccess<PlatformFeature>>('/platform/features', {
        ...payload,
        // The server treats an empty description as absent; sending "" would
        // only make it do that work again.
        description: payload.description || undefined,
      }),
    ),

  /**
   * Corrects a catalogue row's details. The key is not among them — it is what
   * code would gate on, and is fixed once the row exists.
   */
  updateInCatalogue: (uuid: string, payload: CatalogueEditPayload) =>
    unwrap(
      apiClient.patch<ApiSuccess<PlatformFeature>>(`/platform/features/${uuid}`, {
        ...payload,
        description: payload.description || undefined,
      }),
    ),

  /** One institute's tracker — the catalogue with their state marked on it. */
  board: (instituteUuid: string) =>
    unwrap(
      apiClient.get<ApiSuccess<InstituteFeatureBoard>>(
        `/platform/institutes/${instituteUuid}/features`,
      ),
    ),

  /** Records that this institute needs these features. */
  add: (instituteUuid: string, featureUuids: string[]) =>
    unwrap(
      apiClient.post<ApiSuccess<InstituteFeatureBoard>>(
        `/platform/institutes/${instituteUuid}/features`,
        { featureUuids },
      ),
    ),

  /** Sets one pairing's switches. Only the ones named are written. */
  set: (instituteUuid: string, featureUuid: string, payload: FeatureSwitchPayload) =>
    unwrap(
      apiClient.patch<ApiSuccess<InstituteFeatureBoard>>(
        `/platform/institutes/${instituteUuid}/features/${featureUuid}`,
        payload,
      ),
    ),

  /**
   * "Select all" — the same switch across everything this institute is
   * tracking. A verb rather than a list of ids: the button means "all of them",
   * and ids the screen was holding would miss anything added since.
   */
  setAll: (instituteUuid: string, payload: FeatureSwitchPayload) =>
    unwrap(
      apiClient.patch<ApiSuccess<InstituteFeatureBoard>>(
        `/platform/institutes/${instituteUuid}/features/bulk`,
        payload,
      ),
    ),

  /**
   * Copies this institute's requirements onto others.
   *
   * Answers with what each one received rather than the whole board: the boards
   * being written to are not the one on screen.
   */
  copyTo: (instituteUuid: string, payload: FeatureCopyPayload) =>
    unwrap(
      apiClient.post<ApiSuccess<FeatureCopyResult>>(
        `/platform/institutes/${instituteUuid}/features/copy`,
        payload,
      ),
    ),

  /** Withdraws a requirement. Soft — the record of having wanted it stays. */
  remove: (instituteUuid: string, featureUuid: string) =>
    unwrap(
      apiClient.delete<ApiSuccess<InstituteFeatureBoard>>(
        `/platform/institutes/${instituteUuid}/features/${featureUuid}`,
      ),
    ),
};
